import z from "zod";

import {
  documentGroupEnumSchema,
  DocumentGroupEnumType,
  documentTypeEnumSchema,
  patientsApiContract,
} from "@libs/api/patients";
import { HTTPStatusCode } from "@libs/api/types";
import { I18nKeyType } from "@/i18n";

export const presignedUrlForUploadParamsSchema =
  patientsApiContract.uploadedDocuments.generatePresignedUrlForUpload.body;
export type PresignedUrlForUploadParamsType = z.infer<
  typeof presignedUrlForUploadParamsSchema
>;

export const presignedUrlResponseSchema =
  patientsApiContract.uploadedDocuments.generatePresignedUrlForUpload.responses[
    HTTPStatusCode.OK
  ];
export type PresignedUrlResponseType = z.infer<
  typeof presignedUrlResponseSchema
>;

export const addDocumentParamsSchema =
  patientsApiContract.uploadedDocuments.addDocument.body;
export type AddDocumentParamsType = z.infer<typeof addDocumentParamsSchema>;

export const listDocumentsFilterSchema =
  patientsApiContract.uploadedDocuments.listDocuments.query;
export type ListDocumentsFilterType = z.infer<typeof listDocumentsFilterSchema>;

export const documentSchema =
  patientsApiContract.uploadedDocuments.getDocument.responses[
    HTTPStatusCode.OK
  ];
export type DocumentType = z.infer<typeof documentSchema>;

export const documentGroups = documentGroupEnumSchema.options.map(option => ({
  i18nKey: `common:documentGroups.${option}` as I18nKeyType,
  value: option,
}));

type DocumentTypeOptionValueType = z.infer<typeof documentTypeEnumSchema>;
export type DocumentTypeOptionType = {
  i18nKey: I18nKeyType;
  descriptionI18nKey: I18nKeyType;
  value: DocumentTypeOptionValueType;
};

export const documentTypes = documentTypeEnumSchema.options.map(option => ({
  i18nKey: `common:documentTypes.${option}` as I18nKeyType,
  descriptionI18nKey:
    `common:documentTypes.descriptions.${option}` as I18nKeyType,
  value: option,
}));

// Some types need different guidance depending on the section they are
// uploaded under (e.g. SSA statements as proof of a caregiving exemption).
const GROUP_SPECIFIC_DESCRIPTIONS: Partial<
  Record<
    DocumentGroupEnumType,
    { prefix: string; documentTypes: DocumentTypeOptionValueType[] }
  >
> = {
  PROOF_OF_CITIZENSHIP: {
    prefix: "IMMIGRATION",
    documentTypes: ["BIRTH_CERTIFICATE", "PASSPORT"],
  },
  PROOF_OF_EXEMPTION: {
    prefix: "EXEMPTION",
    documentTypes: ["SSA_STATEMENTS", "SSDI_STATEMENTS"],
  },
};

export const getDocumentTypeDescriptionI18nKey = ({
  documentGroup,
  documentType,
}: {
  documentGroup: DocumentGroupEnumType;
  documentType: DocumentTypeOptionValueType;
}): I18nKeyType => {
  const groupSpecific = GROUP_SPECIFIC_DESCRIPTIONS[documentGroup];

  return groupSpecific?.documentTypes.includes(documentType)
    ? (`common:documentTypes.descriptions.${groupSpecific.prefix}_${documentType}` as I18nKeyType)
    : (`common:documentTypes.descriptions.${documentType}` as I18nKeyType);
};

export const documentCategorySchema =
  patientsApiContract.uploadedDocuments.listDocumentCategories.responses[
    HTTPStatusCode.OK
  ];
export type DocumentCategoryType = z.infer<typeof documentCategorySchema>;

// Upload status types
export const uploadStatusSchema = z.enum([
  "UPLOADING",
  "SCANNING",
  "UPLOADED",
  "FAILED",
]);
export type UploadStatusType = z.infer<typeof uploadStatusSchema>;

export type PendingUploadType = {
  id: string;
  documentType: z.infer<typeof documentTypeEnumSchema>;
  documentCategoryId: string;
  householdMemberId: string;
  status: UploadStatusType;
  documentId?: string;
  updatedAt?: string;
  files: {
    uri: string;
    fileName: string;
    mimeType: string;
  }[];
};

export const labelForDocumentGroup = (group: DocumentGroupEnumType) =>
  documentGroups.find(documentGroup => documentGroup.value === group)!.i18nKey;
